/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * BreakDownScreen.tsx - Break-down of answers
*/

import { useMemo, useState } from 'react';
import {
  StyleSheet, Text, View, ScrollView, ImageBackground, Image
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackProps } from '../../navigation/types';
import { containAspectRatio, coverAspectRatio, useNavScreenDimensions } from '../../scale';


const BreakDownScreen = ({ route }: RootStackProps<'BreakDown'>) => {
  const results = route.params;

  const dimens = useNavScreenDimensions();
  const insets = useSafeAreaInsets();

  // Calculate all the quantities we'll need
  const { styles, fontSize } = useMemo(() => {
    // Background image has aspect ratio of 4096/2732 and covers the whole container.
    const backgroundDimens = coverAspectRatio({width: dimens.width, height: dimens.height}, 4096/2732);

    // The rest of the content avoids the left/right safe area insets.
    const safeAreaDimens = { width: dimens.width - insets.left - insets.right, height: dimens.height };

    // We scale the contents of the table  (e.g. font-sizes, images, etc.) as if it is the largest rectangle of
    // aspect-ratio 0.75 contained within the safe area.
    // (Implementation note: later on we allow it to expand vertically for the whole screen, and also allow it to be
    // up to double the width calculated here which looks better on wider screens.)
    const tableDimens = containAspectRatio({width: safeAreaDimens.width, height: safeAreaDimens.height}, 0.75);
    const tinyHeight = 0.3 * tableDimens.width;
    const topPadding = 0.055 * tableDimens.width;
    const sidePadding = 0.02 * tableDimens.width;
    // When scrolled all the way down we need the last element to be in the safe area.
    const bottomPadding = Math.max(sidePadding, insets.bottom);
    const fontSize = 0.032 * tableDimens.width;

    const styles = getBreakDownScreenStyles(
      backgroundDimens, tableDimens, tinyHeight, topPadding, sidePadding, bottomPadding
    );

    return { styles, fontSize, topPadding, sidePadding };
  }, [dimens, insets]);

  return (
    <ImageBackground
      source={require('../../assets/images/BackgroundLandscape.png')}
      style={styles.background}
      imageStyle={styles.backgroundImage}
    >
      {/* Exclude: top (doesn't touch top edge), bottom (allow scrolling to go underneath device notches) */}
      <SafeAreaView style={styles.safeArea} edges={['right', 'left']}>
        <View style={styles.tableContainer}>
          <View style={styles.tiny_view}>
            <Image source={require('../../assets/images/TinyPose_72dpi.png')} style={styles.tiny_image} />
          </View>

          <View style={styles.table_view}>
            <ScrollView overScrollMode="never" contentContainerStyle={styles.scrollViewContainer}>
              <TableHeaderRow fontSize={fontSize}/>

              {results.map((result, i) => (
                <TableRow
                  key={i}
                  fontSize={fontSize}
                  questionText={result.question}
                  answerText={result.answer.toString()}
                  userAnswerText={result.userAnswer?.toString() ?? ''}
                  correct={result.correct}
                  isEven={i%2 === 0}
                  isBottom={i === results.length - 1}
                />
              ))}
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>
    </ImageBackground>
  );
};

export default BreakDownScreen;

const getBreakDownScreenStyles = (
  backgroundDimens: {width: number, height: number},
  tableDimens: {width: number, height: number},
  tinyHeight: number,
  topPadding: number,
  sidePadding: number,
  bottomPadding: number,
) => StyleSheet.create({
  background: {
    flex: 1,
    overflow: 'hidden'
  },

  backgroundImage: {
    width: backgroundDimens.width,
    height: backgroundDimens.height,
    position: 'absolute',
    top: 0,
    // These are set by default but we want to align to top center, so un-set
    bottom: undefined,
    left: undefined,
    right: undefined
  },

  safeArea: {
    flex: 1,
    alignItems: 'center'
  },

  tableContainer: {
    flex: 1,
    width: '100%',
    minWidth: tableDimens.width,
    // For wider screens, it's nice to get the extra space without doing more scaling
    maxWidth: 2 * tableDimens.width,
    alignItems: 'center',
  },

  tiny_view: {
    height: tinyHeight,
    zIndex: 2
  },

  tiny_image: {
    height: '100%',
    aspectRatio: 1,
    resizeMode: 'contain',
    bottom: '-18%',
  },

  table_view: {
    flexShrink: 1,
    width: '92%',
    backgroundColor: '#FFF6A7',
  },

  scrollViewContainer: {
    backgroundColor: '#FFF6A7',
    paddingHorizontal: sidePadding,
    paddingTop: topPadding,
    paddingBottom: bottomPadding,
  }
});

const TableHeaderRow = (props: {
  fontSize: number
}) => {
  const styles = useMemo(() => StyleSheet.create({
    row: {
      flexDirection: 'row',
      backgroundColor: '#8B8FC7'
    },

    text: {
      color: '#fff',
      textAlign: 'center',
      fontFamily: 'WhiteRoseMathsFont',
      fontSize: props.fontSize,
      margin: 0.4 * props.fontSize
    },
  }), [props]);

  return (
    <View style={styles.row}>
      <View style={[sharedStyles.cell, sharedStyles.questionTextCell]}>
        <Text allowFontScaling={false} style={styles.text}>Question</Text>
      </View>
      <View style={[sharedStyles.cell, sharedStyles.actualAnswerCell]}>
        <Text allowFontScaling={false} style={styles.text}>Answer</Text>
      </View>
      <View style={[sharedStyles.cell, sharedStyles.userAnswerCell]}>
        <Text allowFontScaling={false} style={styles.text}>Your Answer</Text>
      </View>
      <View style={[sharedStyles.cell, sharedStyles.endCell, sharedStyles.correctTextCell]}>
        <Text allowFontScaling={false} style={styles.text}>Correct</Text>
      </View>
    </View>
  );
};

const TableRow = (props: {
  fontSize: number,
  questionText: string,
  answerText: string,
  userAnswerText: string,
  correct: boolean,
  isEven: boolean,
  isBottom: boolean,
}) => {
  const [isMouseOver, setMouseOver] = useState(false);

  const styles = useMemo(() => StyleSheet.create({
    row: {
      flexDirection: 'row',
      backgroundColor: isMouseOver ? 'rgb(146, 255, 246)' : (props.isEven ? '#fff' : '#f2f2f2'),
    },

    text: {
      color: '#111',
      textAlign: 'center',
      fontFamily: 'WhiteRoseMathsFont',
      fontSize: props.fontSize,
      margin: 0.4 * props.fontSize
    },

    correctText: {
      color: '#1F1',
      fontSize: 1.5 * props.fontSize,
    },

    wrongText: {
      color: '#F11',
      fontSize: 1.5 * props.fontSize,
    }
  }), [props, isMouseOver]);

  return (
    <View
      style={styles.row}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <View style={[sharedStyles.cell, props.isBottom && sharedStyles.bottomCell, sharedStyles.questionTextCell]}>
        <Text allowFontScaling={false} style={styles.text}>{props.questionText}</Text>
      </View>
      <View style={[sharedStyles.cell, props.isBottom && sharedStyles.bottomCell, sharedStyles.actualAnswerCell]}>
        <Text allowFontScaling={false} style={styles.text}>{props.answerText}</Text>
      </View>
      <View style={[sharedStyles.cell, props.isBottom && sharedStyles.bottomCell, sharedStyles.userAnswerCell]}>
        <Text allowFontScaling={false} style={styles.text}>{props.userAnswerText}</Text>
      </View>
      <View style={[sharedStyles.cell, props.isBottom && sharedStyles.bottomCell, sharedStyles.endCell, sharedStyles.correctTextCell]}>
        <Text allowFontScaling={false} style={[styles.text, props.correct ? styles.correctText : styles.wrongText]}> {props.correct ? '✓' : '✗'} </Text>
      </View>
    </View>
  );
};

const sharedStyles = StyleSheet.create({
  cell: {
    borderStartWidth: StyleSheet.hairlineWidth,
    borderTopWidth: StyleSheet.hairlineWidth,
    justifyContent: 'center'
  },

  endCell: {
    borderEndWidth: StyleSheet.hairlineWidth,
  },

  bottomCell: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  },

  questionTextCell: {
    flex: 4
  },

  userAnswerCell: {
    flex: 2
  },

  actualAnswerCell: {
    flex: 1.8
  },

  correctTextCell: {
    flex: 1.5
  }
});
